import { supabase } from "@/integrations/supabase/client";

/**
 * Picks the post-auth landing path based on `logistics_role` in user_metadata
 * and the company's onboarding state. Shared between AuthCallback (after email
 * verification / OAuth) and Login (after password sign-in) so a user mid-
 * onboarding always lands on the right step instead of a generic /dashboard
 * that has to bounce them again.
 *
 * Best-effort onboarding-complete check: query the company row through both
 * owner and team-member paths. If we can't find a row (timing race with
 * setup-account), fall back to "not onboarded" — HomeGate will redirect
 * again if it turns out the user IS onboarded. One extra redirect is fine.
 *
 * Role resolution prefers the company's stored `logistics_role` over the
 * user_metadata fallback because Google OAuth signups never carry metadata
 * — the canonical role lives on the company row written by setup-account.
 */
export async function chooseLandingPath(
  userId: string,
  role: string | null | undefined,
): Promise<string> {
  let onboarded = false;
  let companyRole: string | null = null;
  try {
    const { data: ownedCompany } = await supabase
      .from("companies")
      .select("onboarding_complete, settings, logistics_role")
      .eq("owner_user_id", userId)
      .maybeSingle();
    let companyRow:
      | { onboarding_complete: boolean | null; settings: unknown; logistics_role: string | null }
      | null = ownedCompany;
    if (!companyRow) {
      const { data: roleRow } = await supabase
        .from("company_roles")
        .select("company_id")
        .eq("user_id", userId)
        .not("accepted_at", "is", null)
        .order("accepted_at", { ascending: false })
        .limit(1)
        .maybeSingle();
      if (roleRow?.company_id) {
        const { data: memberCompany } = await supabase
          .from("companies")
          .select("onboarding_complete, settings, logistics_role")
          .eq("id", roleRow.company_id)
          .maybeSingle();
        if (memberCompany) companyRow = memberCompany;
      }
    }
    if (companyRow) {
      const settings = (companyRow.settings as Record<string, unknown> | null) ?? {};
      onboarded =
        !!companyRow.onboarding_complete || !!settings.onboarding_completed_at;
      companyRole = companyRow.logistics_role ?? null;
    }
  } catch {
    onboarded = false;
  }

  // Onboarded users land on /dash; HomeGate dispatches by logistics_role to
  // CarrierHomePage / BrokerHomePage / DispatcherHomePage.
  if (onboarded) return "/dash";

  const effectiveRole = companyRole ?? role ?? null;
  if (effectiveRole === "broker") return "/dash/onboarding/broker";
  if (effectiveRole === "dispatcher") return "/dash/onboarding";
  if (effectiveRole === "carrier") return "/dash/onboarding/carrier";
  // Other explicit roles fall back to the generic dashboard until they get
  // their own home pages.
  if (effectiveRole) return "/dash";
  // No role anywhere (e.g. Google OAuth signup before setup-account stamps a
  // role) → default to the dispatcher wizard. HomeGate also enforces this
  // belt-and-suspenders so a refresh still lands them in onboarding.
  return "/dash/onboarding";
}
